import { useCreate, useUpdate } from "@refinedev/core";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import type { CommentableType, CommentTag } from "@shared/types";

interface CreateOpts {
  commentableType: CommentableType;
  commentableId: number;
  content: string;
  tag?: CommentTag | null;
  isPinned?: boolean;
  onSuccess?: () => void;
  onError?: () => void;
}

interface UpdateOpts {
  content: string;
  tag?: CommentTag | null;
  isPinned?: boolean;
  onSuccess?: () => void;
  onError?: () => void;
}

interface IdCallback {
  onSuccess?: () => void;
  onError?: () => void;
}

export function useCommentActions() {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const { mutate: createMutation } = useCreate();
  const { mutate: updateMutation } = useUpdate();

  const create = (opts: CreateOpts) => {
    createMutation(
      {
        resource: "comments",
        values: {
          commentable_type: opts.commentableType,
          commentable_id: opts.commentableId,
          content: opts.content.trim(),
          tag: opts.tag ?? null,
          is_pinned: opts.isPinned ?? false,
          is_deleted: false,
        },
        successNotification: false,
      },
      {
        onSuccess: () => {
          message.success(t("comments.messages.create_success"));
          opts.onSuccess?.();
        },
        onError: () => {
          message.error(t("comments.messages.create_error"));
          opts.onError?.();
        },
      },
    );
  };

  const update = (id: number, opts: UpdateOpts) => {
    updateMutation(
      {
        resource: "comments",
        id,
        values: {
          content: opts.content.trim(),
          tag: opts.tag ?? null,
          ...(opts.isPinned !== undefined ? { is_pinned: opts.isPinned } : {}),
        },
      },
      {
        onSuccess: () => {
          message.success(t("comments.messages.update_success"));
          opts.onSuccess?.();
        },
        onError: () => {
          message.error(t("comments.messages.update_error"));
          opts.onError?.();
        },
      },
    );
  };

  const resolve = (id: number, cbs?: IdCallback) => {
    updateMutation(
      {
        resource: "comments",
        id,
        values: { is_pinned: false, resolved_at: new Date().toISOString() },
      },
      {
        onSuccess: cbs?.onSuccess,
        onError: () => {
          message.error(t("comments.messages.pin_error"));
          cbs?.onError?.();
        },
      },
    );
  };

  const pin = (id: number, cbs?: IdCallback) => {
    updateMutation(
      {
        resource: "comments",
        id,
        values: { is_pinned: true },
      },
      {
        onSuccess: cbs?.onSuccess,
        onError: () => {
          message.error(t("comments.messages.pin_error"));
          cbs?.onError?.();
        },
      },
    );
  };

  const unpin = (id: number, cbs?: IdCallback) => {
    updateMutation(
      {
        resource: "comments",
        id,
        values: { is_pinned: false },
      },
      {
        onSuccess: cbs?.onSuccess,
        onError: () => {
          message.error(t("comments.messages.pin_error"));
          cbs?.onError?.();
        },
      },
    );
  };

  const remove = (id: number, cbs?: IdCallback) => {
    updateMutation(
      {
        resource: "comments",
        id,
        values: { is_deleted: true },
      },
      {
        onSuccess: () => {
          message.success(t("comments.messages.delete_success"));
          cbs?.onSuccess?.();
        },
        onError: () => {
          message.error(t("comments.messages.delete_error"));
          cbs?.onError?.();
        },
      },
    );
  };

  return { create, update, resolve, pin, unpin, remove };
}
